import React, { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  Video, ExternalLink, MicOff, VideoOff, PhoneOff,
  Lock, Unlock, Volume2, VolumeX, MoreHorizontal, UserX, UserCheck,
  BarChart2
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/clientWeb/components/ui/dialog"

const AVATAR_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"]

const LIVE_WEBINARS = [
  {
    id: "w1", title: "AI in Modern Healthcare", host: "Sarah Wilson", attendees: 142, started: "2:00 PM",
    participants: [
      { name: "Sarah Wilson", email: "sarah@co.com", isHost: true },
      { name: "James Park",   email: "james@co.com",  isHost: false },
      { name: "Linda Scott",  email: "linda@co.com",  isHost: false },
      { name: "Tom Baker",    email: "tom@co.com",    isHost: false },
      { name: "Ana Rivera",   email: "ana@co.com",    isHost: false },
    ],
  },
  {
    id: "w2", title: "DevOps Best Practices", host: "Mike Chen", attendees: 98, started: "2:30 PM",
    participants: [
      { name: "Mike Chen",   email: "mike@co.com",   isHost: true  },
      { name: "Eve Jones",   email: "eve@co.com",    isHost: false },
      { name: "Robert Wu",   email: "robert@co.com", isHost: false },
      { name: "Maria Lee",   email: "maria@co.com",  isHost: false },
    ],
  },
  {
    id: "w3", title: "Cloud Security Fundamentals", host: "Emma Davis", attendees: 74, started: "3:00 PM",
    participants: [
      { name: "Emma Davis",  email: "emma@co.com",  isHost: true  },
      { name: "Alex Kim",    email: "alex@co.com",  isHost: false },
      { name: "Priya Nair",  email: "priya@co.com", isHost: false },
    ],
  },
]

type WAlertAction =
  | { type: "end_webinar"; webinarId: string; title: string }
  | { type: "mute_all"; webinarId: string; title: string }
  | { type: "lock"; webinarId: string; title: string; locked: boolean }
  | { type: "remove_participant"; webinarId: string; participantName: string }
  | { type: "mute_participant"; webinarId: string; participantName: string; muted: boolean }
  | { type: "disable_video"; webinarId: string; participantName: string; videoOff: boolean }

export default function LiveWebinarsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [wAlert, setWAlert] = useState<WAlertAction | null>(null)
  const [wStates, setWStates] = useState<Record<string, {
    locked: boolean; mutedParticipants: Record<string, boolean>; videoOffParticipants: Record<string, boolean>
  }>>({})

  const getWS = (id: string) => wStates[id] ?? { locked: false, mutedParticipants: {}, videoOffParticipants: {} }
  const patchWS = (id: string, patch: Partial<ReturnType<typeof getWS>>) =>
    setWStates(prev => ({ ...prev, [id]: { ...getWS(id), ...patch } }))

  const confirmAction = () => {
    if (!wAlert) return
    const ws = getWS(wAlert.webinarId)
    if (wAlert.type === "mute_all") {
      const all: Record<string, boolean> = {}
      LIVE_WEBINARS.find(w => w.id === wAlert.webinarId)?.participants.forEach(p => { if (!p.isHost) all[p.name] = true })
      patchWS(wAlert.webinarId, { mutedParticipants: { ...ws.mutedParticipants, ...all } })
    } else if (wAlert.type === "lock") {
      patchWS(wAlert.webinarId, { locked: !ws.locked })
    } else if (wAlert.type === "mute_participant") {
      patchWS(wAlert.webinarId, { mutedParticipants: { ...ws.mutedParticipants, [wAlert.participantName]: !wAlert.muted } })
    } else if (wAlert.type === "disable_video") {
      patchWS(wAlert.webinarId, { videoOffParticipants: { ...ws.videoOffParticipants, [wAlert.participantName]: !wAlert.videoOff } })
    }
    setWAlert(null)
  }

  const alertMeta = wAlert ? (() => {
    if (wAlert.type === "end_webinar") return { icon: <PhoneOff className="w-5 h-5 text-red-500" />, title: t("webinar.endWebinar"), desc: `${t("webinar.endWebinarDesc", { title: wAlert.title })}`, label: t("webinar.endWebinar"), cls: "bg-red-500 hover:bg-red-600 text-white" }
    if (wAlert.type === "mute_all") return { icon: <MicOff className="w-5 h-5 text-orange-500" />, title: t("webinar.muteAll"), desc: `${t("webinar.muteAllDesc", { title: wAlert.title })}`, label: t("webinar.muteAll"), cls: "bg-orange-500 hover:bg-orange-600 text-white" }
    if (wAlert.type === "lock") return { icon: wAlert.locked ? <Unlock className="w-5 h-5 text-blue-500" /> : <Lock className="w-5 h-5 text-blue-500" />, title: wAlert.locked ? t("webinar.unlockWebinar") : t("webinar.lockWebinar"), desc: wAlert.locked ? t("webinar.unlockWebinarDesc") : t("webinar.lockWebinarDesc"), label: wAlert.locked ? t("webinar.unlock") : t("webinar.lock"), cls: "bg-blue-500 hover:bg-blue-600 text-white" }
    if (wAlert.type === "remove_participant") return { icon: <UserX className="w-5 h-5 text-red-500" />, title: t("webinar.removeAttendee"), desc: `${t("webinar.removeAttendeeDesc", { name: wAlert.participantName })}`, label: t("webinar.remove"), cls: "bg-red-500 hover:bg-red-600 text-white" }
    if (wAlert.type === "mute_participant") return { icon: wAlert.muted ? <Volume2 className="w-5 h-5 text-emerald-500" /> : <VolumeX className="w-5 h-5 text-orange-500" />, title: wAlert.muted ? `${t("webinar.unmute", { name: wAlert.participantName })}` : `${t("webinar.mute", { name: wAlert.participantName })}`, desc: wAlert.muted ? `${t("webinar.unmuteDesc", { name: wAlert.participantName })}` : `${t("webinar.muteDesc", { name: wAlert.participantName })}`, label: wAlert.muted ? t("webinar.unmuteLabel") : t("webinar.muteLabel"), cls: "bg-orange-500 hover:bg-orange-600 text-white" }
    if (wAlert.type === "disable_video") return { icon: <VideoOff className="w-5 h-5 text-slate-500 dark:text-slate-400" />, title: wAlert.videoOff ? t("webinar.enableVideo") : t("webinar.disableVideo"), desc: wAlert.videoOff ? `${t("webinar.enableVideoDesc", { name: wAlert.participantName })}` : `${t("webinar.disableVideoDesc", { name: wAlert.participantName })}`, label: wAlert.videoOff ? t("webinar.enableLabel") : t("webinar.disableLabel"), cls: "bg-slate-600 hover:bg-slate-700 text-white" }
    return null
  })() : null

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar/analytics")} className="hover:text-primary text-primary font-semibold transition-colors flex items-center gap-1">
            <BarChart2 className="w-3.5 h-3.5" /> {t("webinar.webinarAnalytics")}
          </button>
          <span>/</span>
          <span className="text-foreground font-medium">{t("webinar.liveWebinars")}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <h1 className="text-xl font-bold text-foreground">{t("webinar.liveWebinars")}</h1>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{LIVE_WEBINARS.length} {t("webinar.webinarsCurrentlyLive")}</p>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <Dialog open={!!wAlert} onOpenChange={() => setWAlert(null)}>
        <DialogContent className="max-w-sm">
          {alertMeta && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">{alertMeta.icon}</div>
                  <DialogTitle>{alertMeta.title}</DialogTitle>
                </div>
                <DialogDescription>{alertMeta.desc}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => setWAlert(null)}>{t("webinar.cancel")}</Button>
                <Button size="sm" className={alertMeta.cls} onClick={confirmAction}>{alertMeta.label}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm overflow-hidden">
        {LIVE_WEBINARS.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Video className="w-16 h-16 text-muted-foreground/20" />
            <p className="text-sm font-semibold text-muted-foreground">{t("webinar.noLiveWebinars")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-stroke-100 dark:border-border dark:border-border bg-slate-50 dark:bg-card/80 hover:bg-transparent">
                  <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.webinarTitle")}</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.host")}</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.attendees")}</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.started")}</TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-28">{t("webinar.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {LIVE_WEBINARS.map((webinar) => {
                  const ws = getWS(webinar.id)
                  const isExpanded = expandedId === webinar.id
                  return (
                    <React.Fragment key={webinar.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => setExpandedId(isExpanded ? null : webinar.id)}
                        className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors border-b border-slate-50 dark:border-border/40 ${isExpanded ? "bg-primary/5" : ""}`}
                      >
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="relative flex h-2 w-2 shrink-0">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            <span className="font-semibold text-foreground truncate max-w-[200px]">{webinar.title}</span>
                            {ws.locked && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                              style={{ background: AVATAR_COLORS[webinar.host.charCodeAt(0) % AVATAR_COLORS.length] }}>
                              {webinar.host.charAt(0)}
                            </div>
                            <span className="text-sm text-foreground">{webinar.host}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex -space-x-1.5">
                              {webinar.participants.slice(0, 3).map((p, idx) => (
                                <div key={idx} className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-card"
                                  style={{ background: AVATAR_COLORS[p.name.charCodeAt(0) % AVATAR_COLORS.length] }} title={p.name}>
                                  {p.name.charAt(0)}
                                </div>
                              ))}
                              {webinar.attendees > 3 && (
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold bg-muted text-muted-foreground border-2 border-card">
                                  +{webinar.attendees - 3}
                                </div>
                              )}
                            </div>
                            <span className="text-sm font-medium text-foreground">{webinar.attendees}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-muted-foreground">{webinar.started}</TableCell>
                        <TableCell className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button size="sm" className="h-7 text-xs gap-1 px-2.5">
                              <ExternalLink className="w-3 h-3" /> {t("webinar.join")}
                            </Button>
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : webinar.id)}
                              className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                            >
                              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </div>
                        </TableCell>
                      </motion.tr>

                      {/* Expanded host controls */}
                      <AnimatePresence>
                        {isExpanded && (
                          <tr key={`${webinar.id}-ctrl`}>
                            <td colSpan={5} className="p-0">
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="px-6 py-5 bg-muted/20 border-b border-border space-y-5">
                                  {/* Webinar-level controls */}
                                  <div>
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("webinar.webinarControls")}</p>
                                    <div className="flex flex-wrap gap-2">
                                      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                                        onClick={() => setWAlert({ type: "mute_all", webinarId: webinar.id, title: webinar.title })}>
                                        <MicOff className="w-3.5 h-3.5 text-orange-500" /> {t("webinar.muteAll")}
                                      </Button>
                                      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                                        onClick={() => setWAlert({ type: "lock", webinarId: webinar.id, title: webinar.title, locked: ws.locked })}>
                                        {ws.locked
                                          ? <><Unlock className="w-3.5 h-3.5 text-blue-500" /> {t("webinar.unlockWebinar")}</>
                                          : <><Lock className="w-3.5 h-3.5 text-blue-500" /> {t("webinar.lockWebinar")}</>}
                                      </Button>
                                      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-900/20"
                                        onClick={() => setWAlert({ type: "end_webinar", webinarId: webinar.id, title: webinar.title })}>
                                        <PhoneOff className="w-3.5 h-3.5" /> {t("webinar.endWebinar")}
                                      </Button>
                                      {ws.locked && <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0">{t("webinar.locked")}</Badge>}
                                    </div>
                                  </div>

                                  {/* Per-participant controls */}
                                  <div>
                                    <div className="flex items-center justify-between mb-3">
                                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("webinar.participants")}</p>
                                      <Badge variant="secondary" className="text-[10px]">{webinar.participants.length} {t("webinar.inWebinar")}</Badge>
                                    </div>
                                    <div className="space-y-1.5">
                                      {webinar.participants.map((att) => {
                                        const isMuted = !!ws.mutedParticipants[att.name]
                                        const isVideoOff = !!ws.videoOffParticipants[att.name]
                                        return (
                                          <div key={att.name} className="flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-2.5 group">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                                              style={{ background: AVATAR_COLORS[att.name.charCodeAt(0) % AVATAR_COLORS.length] }}>
                                              {att.name.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2">
                                                <p className="text-sm font-medium text-foreground truncate">{att.name}</p>
                                                {att.isHost && <Badge className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary border-0">{t("webinar.host")}</Badge>}
                                                {isMuted && <Badge className="text-[9px] px-1.5 py-0 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 border-0">{t("webinar.muted")}</Badge>}
                                                {isVideoOff && <Badge className="text-[9px] px-1.5 py-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-0">{t("webinar.noVideo")}</Badge>}
                                              </div>
                                              <p className="text-[11px] text-muted-foreground truncate">{att.email}</p>
                                            </div>
                                            {!att.isHost && (
                                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button title={isMuted ? t("webinar.unmuteLabel") : t("webinar.muteLabel")}
                                                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                                                  onClick={() => setWAlert({ type: "mute_participant", webinarId: webinar.id, participantName: att.name, muted: isMuted })}>
                                                  {isMuted ? <Volume2 className="w-4 h-4 text-emerald-500" /> : <VolumeX className="w-4 h-4 text-orange-500" />}
                                                </button>
                                                <button title={isVideoOff ? t("webinar.enableVideo") : t("webinar.disableVideo")}
                                                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                                                  onClick={() => setWAlert({ type: "disable_video", webinarId: webinar.id, participantName: att.name, videoOff: isVideoOff })}>
                                                  <VideoOff className={`w-4 h-4 ${isVideoOff ? "text-emerald-500" : "text-slate-400"}`} />
                                                </button>
                                                <button title={t("webinar.removeFromWebinar")}
                                                  className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center transition-colors"
                                                  onClick={() => setWAlert({ type: "remove_participant", webinarId: webinar.id, participantName: att.name })}>
                                                  <UserX className="w-4 h-4 text-red-500" />
                                                </button>
                                              </div>
                                            )}
                                            {att.isHost && (
                                              <UserCheck className="w-4 h-4 text-primary" />
                                            )}
                                          </div>
                                        )
                                      })}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-2">{t("webinar.hoverParticipantReveal")}</p>
                                  </div>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
